import { Component, OnInit, Input } from '@angular/core';
import { GraphClientService } from '../services/graph-client-service';
import { LoginUnawareComponent } from '../login-unaware-component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DiagnosticsServiceFactory } from '../services/diagnostics-service-factory';

const MAX_NUMBER_OF_CATEGORIES = 6;

/**
 * Displays a list of values winners sourced from a SharePoint Online list.
 * The component groups values winners by category after receiving a flat list of items
 * from SharePoint, then displays it inside a fixed number of buckets.
 * When a "bucket" or a "category" contains winner(s) then it is clickable, and shows a
 * modal dialog with detailsabout each winner on click. The dialog is implemented using
 * ng-bootstrap.
 */
@Component({
  selector: 'app-values-winners',
  templateUrl: './values-winners.component.html',
  styleUrls: ['./values-winners.component.scss']
})
export class ValuesWinnersComponent extends LoginUnawareComponent implements OnInit {
  @Input() public title: string;
  public winnersDetail: { category: string, winners: Array<any> };
  private winnersMap: any;

  constructor(
    diagnosticsFactory: DiagnosticsServiceFactory,
    private graphClient: GraphClientService,
    private modalService: NgbModal) {
    super(diagnosticsFactory.create('ValuesWinnersComponent'));
    this.winnersMap = {};
  }

  ngOnInit() {
    this.graphClient.getData(this.constructor.name).subscribe(
      data => {
        this.winnersMap = this.getWinnersMap(data);
      }, error => {
      this.diagnostics.logError('Error accessing Values Winners list: ' + JSON.stringify(error));
    });
  }

  openDialog(template: any, index: number) {
    if (this.categoryHasWinners(index)) {
      const category = this.getCategory(index);
      const winners: Array<any> = this.winnersMap[category];
      this.winnersDetail = {category: category, winners: winners };
      this.modalService.open(template, {size: 'lg', centered: true});
    }
  }

  getWinners(index: number): any[] {
    const keys = Object.keys(this.winnersMap);
    if (index >= keys.length) {
      return [];
    } else {
      const key = keys[index];
      return this.winnersMap[key];
    }
  }

  getCategory(index: number): string {
    const keys = Object.keys(this.winnersMap);
    if (index >= keys.length) {
      return '';
    } else {
      const key = keys[index];
      return key;
    }
  }

  categoryHasWinners(index: number): boolean {
    const winners = this.getWinners(index);

    if (winners.length > 1) {
      return true;
    } else if (winners.length === 0) {
      return false;
    } else if (winners[0].details && winners[0].details.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  private getWinnersMap(data: Object) {
    const dynamicData = data as any;

    if (!dynamicData.value || 0 === dynamicData.value.length) {
      const message = 'Error parsing returned object from Values Winners list.';
      this.diagnostics.logError(message);
      throw new Error(message);
    }

    const items: any[] = dynamicData.value;
    const categoryMap = items.reduce((categories, item) => {
      const category = item.fields.Category;
      const winner = {
        title: item.fields.Title,
        department: item.fields.Department,
        details: item.fields.Details
      };
      let winners = categories[category] as any[];

      if (!winners) {
        winners = [];
        categories[category] = winners;
      }

      winners.push(winner);
      return categories;
    }, {});

    const delta = Object.keys(categoryMap).length - MAX_NUMBER_OF_CATEGORIES;

    if (delta > 0) {
      for (let i = 0; i < delta; ++i) {
        const key = Object.keys(categoryMap).pop();
        this.diagnostics.logWarning(
          `Component supports a
          maximum of ${MAX_NUMBER_OF_CATEGORIES} categories.
          Category '${key}' will not be displayed.`);
        delete categoryMap[key];
      }
    }

    return categoryMap;
  }
}
